import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { WaterBasic, WatersService } from '@services/waters.service';
import { debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'app-water-selector-dialog',
  templateUrl: './water-selector-dialog.component.html',
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaterSelectorDialogComponent {
  private readonly watersService = inject(WatersService);
  private readonly dialogRef = inject(MatDialogRef<WaterSelectorDialogComponent>);
  private readonly toastService = inject(ToastService);

  readonly searchControl = new FormControl('');
  readonly waters = signal<WaterBasic[]>([]);
  readonly hasMore = signal(false);
  readonly loading = signal(false);
  selectedWater: WaterBasic | null = null;

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        startWith(''),
        map((value) => value?.trim()),
        distinctUntilChanged(),
        debounceTime(300),
        switchMap((search) => {
          this.loading.set(true);
          return this.watersService.getWaters(search || undefined).pipe(
            showGenericErrorToast(this.toastService, () => undefined),
            finalize(() => {
              this.loading.set(false);
            }),
          );
        }),
      )
      .subscribe((waters) => {
        this.waters.set(waters?.waters || []);
        this.hasMore.set(waters?.hasMore || false);
      });
  }

  confirm() {
    if (this.selectedWater) {
      this.dialogRef.close(this.selectedWater);
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
